// Slice H2 — curated strategy template types.
//
// These templates ship with the app (NOT Supabase-backed — that's the
// user-generated TemplateBrowser surface). Static TS modules; instant
// load; bundled with the app. Each template is a hand-curated starting
// point new users can load + backtest without authoring blocks from
// scratch.
//
// Design contract (see docs/H2_TEMPLATE_GALLERY_DESIGN.md):
//   • Templates are STARTING POINTS, not guaranteed profitable systems.
//   • Each template's `expectedValidation` is honest — typically
//     yellow / cautious / regime-dependent. Never "robust on every
//     asset."
//   • Every template includes at least one risk_rule block.
//   • Block IDs are namespaced by slug so two templates loaded in
//     sequence can't collide on IDs.
//   • No Supabase roundtrip, no marketplace, no profit claims.

import type { Block } from '../types';

/** Strategy archetype. Drives the gallery filter UI + H1's eventual
 *  cross-link (H2.1 follow-up) so suggestions like "Try a different
 *  family" can route users back to the right gallery slice. */
export type TemplateFamily =
  | 'trend'
  | 'mean-reversion'
  | 'breakout'
  | 'prop-firm';

/** Pre-attached prop-firm profile reference. Templates can either
 *  embed the full profile in the strategy_config block (preferred)
 *  or reference a provider/preset that the engine resolves. The
 *  FTMO Intraday Compliant template uses the embedded approach for
 *  parity with existing FTMO-sweep template behavior. */
export interface TemplatePropFirmRef {
  provider: 'ftmo' | 'goat';
  preset: string;
}

/** A curated starter template. Each template is a complete TSL
 *  block graph with sensible defaults + risk controls + honest
 *  user-facing copy describing what it is + isn't. */
export interface CuratedTemplate {
  /** Stable kebab-case identifier. Used as the block-id prefix
   *  (`<slug>:block-name`) and as the analytics + telemetry key. */
  slug: string;

  /** Human-readable name. Renders as the card title. May include
   *  a version suffix ("EMA Pullback Trend-Follower (v1)"). */
  name: string;

  /** Family bucket — filter pill in the gallery + lookup key for
   *  H1's eventual cross-link. */
  family: TemplateFamily;

  /** ≤120-char one-line tagline for the card body.
   *  e.g. "Trades pullbacks to the slow EMA inside a confirmed uptrend." */
  bestFor: string;

  /** 2-3 sentence expansion shown when the user expands the card.
   *  Should describe the LOGIC, not promote returns. */
  detail: string;

  /** Asset class guidance — what markets the template was designed for.
   *  Honest about edge cases ("avoid heavy-trending pairs"). */
  assetGuidance: string;

  /** Recommended timeframes — chips on the card. The strategy may
   *  technically run on any TF, but performance varies. */
  timeframeGuidance: string[];

  /** Honest caveats — rendered as warning chips on the card.
   *  e.g. "Fragile in chop", "Expects directional bias". */
  riskNotes: string[];

  /** Realistic WFA expectation. NEVER "this template is robust" —
   *  always something like "Green on trending samples; yellow in chop". */
  expectedValidation: string;

  /** The TSL block graph itself. Includes:
   *    • strategy_config (with sensible initial_capital / costs)
   *    • indicator / logic blocks
   *    • entry / exit / stop_loss / take_profit terminals
   *    • at least one risk_rule block (max_risk_per_trade_pct_v1 or similar)
   *  Block IDs prefixed by slug so loading two templates in sequence
   *  can't collide. */
  blocks: Block[];

  /** Version label. Bumped when block structure changes meaningfully.
   *  Rendered as "(v1)" suffix on the card name. */
  version: string;

  /** Optional — for the prop-firm template, declares the provider +
   *  preset so the gallery card can render a compliance chip. The
   *  actual rules ride on the strategy_config block's `propFirm`
   *  field (engine-resolved). */
  propFirm?: TemplatePropFirmRef;
}

/** Helper — build the default metadata fields every Block needs.
 *  Templates use a fixed creation timestamp so the `loadedAt` /
 *  `dirty` heuristics in StrategyContext don't false-positive. */
export function templateBlockMetadata(description: string): Block['metadata'] {
  // Use a stable epoch so re-loading the template doesn't change the
  // block timestamps. StrategyContext + the replace-guard look at
  // loadedAt (a context-level field), NOT block.metadata.modified,
  // so a stable timestamp here is safe.
  const STABLE_EPOCH = new Date('2026-01-01T00:00:00Z');
  return {
    version: '1.0.0',
    author: 'Tradeatory (curated)',
    description,
    tags: ['curated', 'template'],
    created: STABLE_EPOCH,
    modified: STABLE_EPOCH,
    locked: false,
    collapsed: false,
  };
}

/** Helper — namespace a raw block id with the template's slug so
 *  loading two templates in sequence can't collide on IDs. Used by
 *  every template module's local `id(...)` helper. */
export function namespacedBlockId(slug: string, name: string): string {
  return `${slug}:${name}`;
}
